/**
 * ProfilePage
 * User profile settings — accessible via the avatar menu in the header.
 */
import { useEffect, useState } from 'react';
import { useLingui } from '@lingui/react';
import { useUser } from '@clerk/clerk-react';
import { useApiClient } from '../api/client';
import type { UserProfile } from '../api/client';
import { SUPPORTED_DISPLAY_CURRENCIES } from '@wealix/shared';
import { getCurrencyName } from '@wealix/shared';
import CurrencyFlag from '../components/CurrencyFlag';

export default function ProfilePage() {
  const { _ } = useLingui();
  const { user } = useUser();
  const api = useApiClient();

  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    void api
      .getProfile()
      .then((p) => setProfile(p))
      .finally(() => setLoading(false));
  }, [api]);

  async function handleCurrencyChange(currency: string) {
    setSaving(true);
    setSaved(false);
    try {
      const updated = await api.updateProfile(currency);
      setProfile(updated);
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-96">
        <span className="loading loading-spinner loading-lg" />
      </div>
    );
  }

  const preferredCurrency = profile?.preferredCurrency ?? 'USD';

  return (
    <div className="max-w-xl mx-auto space-y-6">
      {/* Account info */}
      <div className="card bg-base-100 shadow border border-base-300">
        <div className="card-body gap-1">
          <h2 className="card-title text-base">{_({ id: 'profile.account.title' })}</h2>
          <p className="text-sm text-base-content/70">{user?.fullName ?? '—'}</p>
          <p className="text-sm text-base-content/50">
            {user?.primaryEmailAddress?.emailAddress ?? '—'}
          </p>
        </div>
      </div>

      {/* Currency preference */}
      <div className="card bg-base-100 shadow border border-base-300">
        <div className="card-body">
          <h2 className="card-title text-base">{_({ id: 'profile.currency.title' })}</h2>
          <p className="text-sm text-base-content/60 mb-3">
            {_({ id: 'profile.currency.description' })}
          </p>

          <div className="flex items-center gap-3">
            <CurrencyFlag
              currency={preferredCurrency}
              className="w-8 h-5 rounded-sm shadow-sm shrink-0"
            />
            <select
              className="select select-bordered w-48"
              value={preferredCurrency}
              disabled={saving}
              onChange={(e) => void handleCurrencyChange(e.target.value)}
            >
              {SUPPORTED_DISPLAY_CURRENCIES.map((c) => (
                <option key={c} value={c}>
                  {c} — {getCurrencyName(c) ?? c}
                </option>
              ))}
            </select>

            {saving && <span className="loading loading-spinner loading-sm" />}
            {saved && (
              <span className="text-success text-sm font-medium">
                {_({ id: 'profile.currency.saved' })}
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
